import type { Account } from '@xinfer/api-client';

/**
 * The plan's limits, in the terms the gate actually enforces them.
 *
 * Concrete numbers are resolved by the control plane and pushed to the serving
 * path, so what is described here is what will actually happen to a request.
 */
export function Limits({ account }: { account: Account }) {
  return (
    <>
      <h1>Limits</h1>
      <p className="subtitle">
        Plan <strong>{account.plan}</strong>
      </p>

      <table>
        <thead>
          <tr>
            <th>Limit</th>
            <th>What it protects</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Concurrent requests</td>
            <td className="muted">
              KV memory directly. This is the binding resource: the node holds a bounded number
              of decoding streams at once regardless of how fast anyone is typing.
            </td>
          </tr>
          <tr>
            <td>Maximum context</td>
            <td className="muted">
              The attention path in use. A prompt longer than this is refused rather than served
              slowly, because the slow path would take minutes and hold KV the whole time.
            </td>
          </tr>
          <tr>
            <td>Concurrent long sessions</td>
            <td className="muted">
              Capped separately from ordinary requests. One 512k-context session is worth about
              twenty ordinary ones, so a single quota covering both would misprice it.
            </td>
          </tr>
          <tr>
            <td>Default output cap</td>
            <td className="muted">
              Applied when a request does not set <code>max_tokens</code> itself.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>What happens when you exceed one</h2>
      <p className="muted">
        Concurrency and quota rejections are <code>429</code>, and are safe to retry with
        backoff. A prompt over the context cap is <code>400</code> and retrying will not help.
        A <code>503</code> means the service is at capacity rather than that anything is wrong
        with the request.
      </p>
    </>
  );
}
